/**
 * Pole-side distribution gear with figures lifted from the manufacturers'
 * own pages and datasheets on 2026-09-05: fibre media converters, DC supplies
 * for cameras and pole boxes, PoE injectors, outdoor enclosures. Every entry
 * carries a `// SRC:` line naming where each figure was read; a figure the
 * source does not print is left out rather than guessed (the TP-Link
 * converters, for one, publish no launch power or sensitivity, so the fibre
 * budget check will say "assumed" on a link they end).
 *
 * Distributor pages are used only where they quote the manufacturer sheet
 * verbatim and are noted as such.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const POLE_DISTRIBUTION_CATALOG: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // Media converters
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.omadanetworks.com/en/business-networking/omada-accessory-media-converter/mc210cs/
  //      "Single-mode Fiber", "2x 1000M SC/UPC ports", "1310nm", "Extends fiber
  //      distance up to 20km", "1x 10/100/1000Mbps Auto-Negotiation RJ45 port",
  //      "External Power Adapter", "Max. Power Consumption 1.8W",
  //      "0~50C", "94.5*73.0*27.0 mm". No launch power / sensitivity published.
  {
    id: 'tplink-mc210cs',
    manufacturer: 'TP-Link',
    model: 'MC210CS',
    category: 'media-converter',
    tagline: 'Gigabit single-mode converter, SC/UPC, 1310 nm, up to 20 km, 1 x RJ45 10/100/1000',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/en/business-networking/omada-accessory-media-converter/mc210cs/',
    specs: {
      fiberMode: 'SM',
      fiberConnector: 'SC',
      wavelengthNm: 1310,
      maxDistanceKm: 20,
      fiberPorts: 1,
      copperPorts: 1,
      copperSpeed: '1G',
      inputVoltage: 'External power adapter',
      powerConsumptionW: 1.8,
      formFactor: 'desktop',
      operatingTempC: '0 to 50',
    },
  },
  // SRC: https://www.omadanetworks.com/en/business-networking/omada-accessory-media-converter/mc220l/
  //      "1x 1000M SFP port", "Multi-mode/Single-mode SFP module", "Maximum
  //      transmission distance depends on the inserting SFP module",
  //      "1x 10/100/1000M RJ45 port", "External Power Adapter", "1.7W",
  //      "0~50C", "94.5*73.0*27.0 mm".
  {
    id: 'tplink-mc220l',
    manufacturer: 'TP-Link',
    model: 'MC220L',
    category: 'media-converter',
    tagline: 'Gigabit converter with SFP slot (SM or MM module sets the optics), 1 x RJ45 10/100/1000',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/en/business-networking/omada-accessory-media-converter/mc220l/',
    specs: {
      fiberMode: 'SM',
      fiberConnector: 'SFP-slot',
      fiberPorts: 1,
      copperPorts: 1,
      copperSpeed: '1G',
      inputVoltage: 'External power adapter',
      powerConsumptionW: 1.7,
      formFactor: 'desktop',
      operatingTempC: '0 to 50',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // DC power supplies
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.meanwell.com/Upload/PDF/HDR-60/HDR-60-SPEC.PDF (2026-04-03)
  //      HDR-60-12: "12V", "4.5A", "54W"; input "85 ~ 264VAC"; efficiency
  //      "88%"; dimension "52.5*90*54.5mm"; "Ultra slim design with 52.5mm
  //      (3SU) width"; working temp "-30 ~ +70C".
  {
    id: 'meanwell-hdr-60-12',
    manufacturer: 'Mean Well',
    model: 'HDR-60-12',
    category: 'power-supply',
    tagline: '12 V / 4.5 A (54 W) ultra-slim DIN rail supply, 3 modules wide, 85-264 V AC in',
    verified: true,
    datasheetUrl: 'https://www.meanwell.com/Upload/PDF/HDR-60/HDR-60-SPEC.PDF',
    specs: {
      outputVoltageV: 12,
      outputA: 4.5,
      outputW: 54,
      channels: 1,
      inputVoltage: '85-264 V AC',
      efficiencyPct: 88,
      formFactor: 'din-rail',
      operatingTempC: '-30 to 70',
    },
  },
  // SRC: same sheet. HDR-60-24: "24V", "2.5A", "60W", efficiency "90%".
  {
    id: 'meanwell-hdr-60-24',
    manufacturer: 'Mean Well',
    model: 'HDR-60-24',
    category: 'power-supply',
    tagline: '24 V / 2.5 A (60 W) ultra-slim DIN rail supply, 3 modules wide, 85-264 V AC in',
    verified: true,
    datasheetUrl: 'https://www.meanwell.com/Upload/PDF/HDR-60/HDR-60-SPEC.PDF',
    specs: {
      outputVoltageV: 24,
      outputA: 2.5,
      outputW: 60,
      channels: 1,
      inputVoltage: '85-264 V AC',
      efficiencyPct: 90,
      formFactor: 'din-rail',
      operatingTempC: '-30 to 70',
    },
  },
  // SRC: same sheet. HDR-60-48: "48V", "1.25A", "60W", efficiency "91%".
  {
    id: 'meanwell-hdr-60-48',
    manufacturer: 'Mean Well',
    model: 'HDR-60-48',
    category: 'power-supply',
    tagline: '48 V / 1.25 A (60 W) ultra-slim DIN rail supply for PoE injectors and converters',
    verified: true,
    datasheetUrl: 'https://www.meanwell.com/Upload/PDF/HDR-60/HDR-60-SPEC.PDF',
    specs: {
      outputVoltageV: 48,
      outputA: 1.25,
      outputW: 60,
      channels: 1,
      inputVoltage: '85-264 V AC',
      efficiencyPct: 91,
      formFactor: 'din-rail',
      operatingTempC: '-30 to 70',
    },
  },
  // SRC: https://www.meanwell.com/Upload/PDF/NDR-120/NDR-120-SPEC.PDF (2026-04-03)
  //      NDR-120-24: "24V", "5A", "120W"; input "90 ~ 264VAC"; efficiency
  //      "88%"; dimension "40*125.2*113.5mm"; working temp "-20 ~ +70C".
  {
    id: 'meanwell-ndr-120-24',
    manufacturer: 'Mean Well',
    model: 'NDR-120-24',
    category: 'power-supply',
    tagline: '24 V / 5 A (120 W) industrial DIN rail supply, metal housing, 90-264 V AC in',
    verified: true,
    datasheetUrl: 'https://www.meanwell.com/Upload/PDF/NDR-120/NDR-120-SPEC.PDF',
    specs: {
      outputVoltageV: 24,
      outputA: 5,
      outputW: 120,
      channels: 1,
      inputVoltage: '90-264 V AC',
      efficiencyPct: 88,
      formFactor: 'din-rail',
      operatingTempC: '-20 to 70',
    },
  },
  // SRC: same sheet. NDR-120-48: "48V", "2.5A", "120W", efficiency "89%".
  {
    id: 'meanwell-ndr-120-48',
    manufacturer: 'Mean Well',
    model: 'NDR-120-48',
    category: 'power-supply',
    tagline: '48 V / 2.5 A (120 W) industrial DIN rail supply for PoE injectors and converters',
    verified: true,
    datasheetUrl: 'https://www.meanwell.com/Upload/PDF/NDR-120/NDR-120-SPEC.PDF',
    specs: {
      outputVoltageV: 48,
      outputA: 2.5,
      outputW: 120,
      channels: 1,
      inputVoltage: '90-264 V AC',
      efficiencyPct: 89,
      formFactor: 'din-rail',
      operatingTempC: '-20 to 70',
    },
  },
  // SRC: https://www.pulsar.pl/pl/karta/PSDC04124.pdf (manufacturer sheet), figures
  //      as quoted verbatim by distributors ivolta.pl and alkam-security.pl:
  //      "4 x 1A/12VDC outputs", "4 outputs independently protected with
  //      fuses F 1A or PTC 1A", input "85-264VAC / 120-370VDC", "maximum power
  //      60W", "80% efficiency", "metal enclosure", output adjustable "12-15V".
  {
    id: 'pulsar-psdc04124',
    manufacturer: 'Pulsar',
    model: 'PSDC04124',
    category: 'power-supply',
    tagline: '12 V / 4 A supply for four cameras, 4 x 1 A fused outputs, metal enclosure, 85-264 V AC in',
    verified: true,
    datasheetUrl: 'https://www.pulsar.pl/pl/karta/PSDC04124.pdf',
    specs: {
      outputVoltageV: 12,
      outputA: 4,
      outputW: 60,
      channels: 4,
      perChannelA: 1,
      inputVoltage: '85-264 V AC',
      efficiencyPct: 80,
      formFactor: 'enclosure',
      enclosureIncluded: true,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // PoE injectors
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://store.ui.com/us/en/products/u-poe-at
  //      "up to 30W of PoE+", "48V DC @ 0.65A", Gigabit LAN port,
  //      "100-240V AC at 50/60Hz", "93 x 62 x 35 mm", "0 to 40 C".
  {
    id: 'ubiquiti-u-poe-at-injector',
    manufacturer: 'Ubiquiti',
    model: 'U-POE-at',
    category: 'poe-injector',
    tagline: 'Single-port 802.3at PoE+ injector, 30 W (48 V / 0.65 A), gigabit pass-through, mains brick',
    verified: true,
    datasheetUrl: 'https://store.ui.com/us/en/products/u-poe-at',
    specs: {
      ports: 1,
      poeStandard: '802.3at',
      poePerPortW: 30,
      poeBudgetW: 30,
      dataSpeed: '1G',
      inputVoltage: '100-240 V AC',
      formFactor: 'brick',
      operatingTempC: '0 to 40',
    },
  },
  // SRC: https://www.omadanetworks.com/en/business-networking/omada-accessory-poe-adapter/poe160s/
  //      "IEEE802.3af, IEEE802.3at", "max. 30 W", "10/100/1000Mbps" data-in
  //      and power+data-out, "100-240 V, 50/60 Hz", "0C to 40C",
  //      "125x59.4x36.8 mm".
  {
    id: 'tplink-tl-poe160s',
    manufacturer: 'TP-Link',
    model: 'TL-POE160S',
    category: 'poe-injector',
    tagline: 'Single-port 802.3af/at PoE+ injector, 30 W, gigabit pass-through, mains brick',
    verified: true,
    datasheetUrl: 'https://www.omadanetworks.com/en/business-networking/omada-accessory-poe-adapter/poe160s/',
    specs: {
      ports: 1,
      poeStandard: '802.3at',
      poePerPortW: 30,
      poeBudgetW: 30,
      dataSpeed: '1G',
      inputVoltage: '100-240 V AC',
      formFactor: 'brick',
      operatingTempC: '0 to 40',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Outdoor enclosures
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://www.fibox.com/products/arca-iec/arca-403021
  //      ARCA 403021: 400 x 300 x 210 mm, polycarbonate, IP66, IK10, RAL 7035,
  //      temperature range -40 to 80 C, 2-point lock; distributor sheets
  //      (Newark 49X9172) quote the same figures.
  {
    id: 'fibox-arca-403021',
    manufacturer: 'Fibox',
    model: 'ARCA 403021',
    category: 'pole-enclosure',
    tagline: 'Polycarbonate enclosure 400 x 300 x 210 mm, IP66 / IK10, opaque door, 2-point lock, RAL 7035',
    verified: true,
    datasheetUrl: 'https://www.fibox.com/products/arca-iec/arca-403021',
    specs: {
      widthMm: 300,
      heightMm: 400,
      depthMm: 210,
      ipRating: 'IP66',
      ikRating: 'IK10',
      material: 'polycarbonate',
      mounting: ['pole', 'wall'],
      lock: true,
      colour: 'RAL 7035',
      operatingTempC: '-40 to 80',
    },
  },
  // SRC: https://www.se.com/us/en/product/NSYPLM43G/ (Thalassa PLM, plain door)
  //      "430x330x200mm", "IP66", "IK10", "RAL 7035", polyester reinforced with
  //      fibreglass, 2 locks (3 mm double-bar), without mounting plate.
  {
    id: 'schneider-nsyplm43g',
    manufacturer: 'Schneider Electric',
    model: 'NSYPLM43G',
    category: 'pole-enclosure',
    tagline: 'Thalassa PLM fibreglass-polyester enclosure 430 x 330 x 200 mm, IP66 / IK10, plain door, RAL 7035',
    verified: true,
    datasheetUrl: 'https://www.se.com/us/en/product/NSYPLM43G/',
    specs: {
      widthMm: 330,
      heightMm: 430,
      depthMm: 200,
      ipRating: 'IP66',
      ikRating: 'IK10',
      material: 'fibreglass',
      mounting: ['wall', 'pole'],
      lock: true,
      colour: 'RAL 7035',
    },
  },
];
